// src/pages/Blog.js
import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import tw, { css } from "twin.macro";
import styled from "styled-components";

import AnimationRevealPage from "helpers/AnimationRevealPage";   // ✅ match other pages
import Header from "components/headers/light";
import Footer from "components/footers/SimpleFiveColumn";        // or use FiveColumnWithInputForm to match Contact
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import { SectionHeading } from "components/misc/Headings";
import { SectionDescription } from "components/misc/Typography";
import { blogPosts } from "blogs";
import { css as cssPropMacro } from "styled-components/macro"; //eslint-disable-line -- enables the css/tw prop in this file

/* --- Page heading --- */
const HeadingContainer = tw.div`text-center`;
const Heading = tw(SectionHeading)`text-primary-900`;
const Description = tw(SectionDescription)`mx-auto`;

/* --- Filters bar --- */
const ControlsBar = tw.div`mt-8 mb-4 flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between`;
const LeftControls = tw.div`flex-1 flex gap-3 items-center`;
const RightControls = tw.div`flex gap-3 items-center`;
const SelectWrapper = tw.div`relative inline-block`;
const Select = tw.select`
  appearance-none px-3 pr-8 py-2
  rounded-md border border-gray-300 bg-white text-sm
`;
const SelectArrow = tw.span`
  pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-500
`;
const Input = tw.input`px-3 py-2 rounded-md border border-gray-300 bg-white w-full md:w-72`;

/* --- Grid & Cards (matches homepage) --- */
const Posts = tw.div`mt-8 flex flex-wrap -mr-3 relative`;
const Post = tw(Link)`flex flex-col h-full bg-gray-200 rounded overflow-hidden focus:outline-none focus:shadow-outline`;
const PostImage = styled.div(({ imageSrc }) => [
  css`background-image: url("${imageSrc || ""}");`,
  tw`h-64 sm:h-80 bg-center bg-cover`
]);
const PostText = tw.div`flex-1 px-6 py-8`;
const PostTitle = tw.h6`font-bold hover:text-primary-500 transition duration-300`;
const PostDescription = tw.p``;

const PostContainer = styled.div(({ featured }) => [
  tw`relative z-20 mt-10 sm:pt-3 pr-3 w-full sm:w-1/2 lg:w-1/3 max-w-sm mx-auto sm:max-w-none sm:mx-0`,
  featured && css`
    ${tw`w-full sm:w-full lg:w-2/3`}
    ${Post} { ${tw`sm:flex-row items-center sm:pr-3`} }
    ${PostImage} { ${tw`sm:h-80 sm:min-h-full w-full sm:w-1/2`} }
    ${PostText} { ${tw`pl-8 pr-5`} }
    ${PostTitle} { ${tw`text-2xl mt-8`} }
    ${PostDescription} { ${tw`mt-8 mb-8 text-sm font-semibold text-gray-600 leading-relaxed`} }
  `
]);


/* --- Pagination --- */
const PaginationBar = tw.nav`mt-10 flex flex-wrap items-center justify-center gap-2`;
const PageBtn = tw.button`px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed`;
const PageNumber = styled.button(({ active }) => [
  tw`px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-100 transition`,
  active && tw`bg-primary-500 text-white border-primary-500 hover:bg-primary-600`
]);

function parseDate(d) { return d ? new Date(d) : new Date(0); }

export default function BlogPage() {
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("newest");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 9;

  const categories = useMemo(() => {
    const set = new Set();
    blogPosts.forEach(p => {
      const c = p.category || p.frontmatter?.category;
      if (c) set.add(c);
    });
    return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, []);

  const normalized = useMemo(() => {
    return blogPosts.map(p => ({
      slug: p.slug,
      title: p.title || p.frontmatter?.title || "Untitled",
      image: p.image || p.frontmatter?.image,
      description: p.description || p.frontmatter?.description || "",
      category: p.category || p.frontmatter?.category || "General",
      date: p.date || p.frontmatter?.date || "",
      featured: p.featured || p.frontmatter?.featured || false,
    }));
  }, []);

  const filteredSorted = useMemo(() => {
    let list = [...normalized];
    if (category !== "All") list = list.filter(p => p.category === category);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter(p =>
      p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
    );
    if (sort === "newest") list.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    else if (sort === "oldest") list.sort((a, b) => parseDate(a.date) - parseDate(b.date));
    else if (sort === "az") list.sort((a, b) => a.title.localeCompare(b.title));
    return list;
  }, [normalized, category, sort, query]);

  useEffect(() => { setPage(1); }, [category, sort, query]);

  const pageCount = Math.max(1, Math.ceil(filteredSorted.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * pageSize;
  const paged = filteredSorted.slice(start, start + pageSize);

  const pageNumbers = useMemo(() => {
    const nums = [];
    const maxToShow = 7;
    if (pageCount <= maxToShow) {
      for (let i = 1; i <= pageCount; i++) nums.push(i);
    } else {
      const left = Math.max(2, currentPage - 2);
      const right = Math.min(pageCount - 1, currentPage + 2);
      nums.push(1);
      if (left > 2) nums.push("…");
      for (let i = left; i <= right; i++) nums.push(i);
      if (right < pageCount - 1) nums.push("…");
      nums.push(pageCount);
    }
    return nums;
  }, [currentPage, pageCount]);

  return (
    <AnimationRevealPage>
      <Helmet>
        <title>Blog | EthosHub</title>
        <meta name="description" content="Insights, guides and updates on workplace culture, anonymous reporting, HR compliance, and the Worker Protection Act from the EthosHub team." />
        <link rel="canonical" href="https://www.ethoshub.io/blog" />
        <meta property="og:title" content="Blog | EthosHub" />
        <meta property="og:description" content="Insights on workplace culture, anonymous reporting, HR compliance, and the Worker Protection Act." />
        <meta property="og:url" content="https://www.ethoshub.io/blog" />
      </Helmet>
      <Header />
      <Container>
        <ContentWithPaddingXl>
          <HeadingContainer>
            <Heading>Latest from the blog</Heading>
            <Description>Read the newest insights, guides and updates.</Description>
          </HeadingContainer>

          {/* Filters */}
          <ControlsBar>
            <LeftControls>
              <SelectWrapper>
                <Select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Filter by category">
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
                <SelectArrow>▼</SelectArrow>
              </SelectWrapper>

              <SelectWrapper>
                <Select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort posts">
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="az">A–Z</option>
                </Select>
                <SelectArrow>▼</SelectArrow>
              </SelectWrapper>
            </LeftControls>

            <RightControls>
              <Input
                type="search"
                placeholder="Search posts…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </RightControls>
          </ControlsBar>

          {/* Grid */}
          <Posts>
            {paged.map((post, index) => (
              <PostContainer key={post.slug || index} featured={!!post.featured}>
                <Post to={`/blog/${post.slug}`}>
                  <PostImage imageSrc={post.image} />
                  <PostText>
                    <div tw="text-xs uppercase tracking-wide text-gray-600 mb-2">
                      {post.category}
                      {post.date && <span tw="text-gray-400"> • {new Date(post.date).toLocaleDateString()}</span>}
                    </div>
                    <PostTitle>{post.title}</PostTitle>
                    {post.featured && post.description && (
                      <PostDescription tw="mt-2 text-gray-700">{post.description}</PostDescription>
                    )}
                    {post.featured && (
                      <div tw="mt-6">
                        <span tw="inline-block px-5 py-3 text-sm font-semibold text-white bg-primary-500 rounded hover:bg-primary-700 transition duration-300">
                          Read Blog
                        </span>
                      </div>
                    )}
                  </PostText>
                </Post>
              </PostContainer>
            ))}

          </Posts>

          {/* Pagination */}
          <PaginationBar aria-label="Blog pagination">
            <PageBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>← Prev</PageBtn>
            {pageNumbers.map((n, i) =>
              n === "…" ? (
                <span key={`ellipsis-${i}`} tw="px-2 select-none">…</span>
              ) : (
                <PageNumber key={n} active={n === currentPage} onClick={() => setPage(n)}>
                  {n}
                </PageNumber>
              )
            )}
            <PageBtn onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={currentPage === pageCount}>Next →</PageBtn>
          </PaginationBar>
        </ContentWithPaddingXl>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
}